import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/collapsible";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

type MetricItem = {
  id: string;
  label: string;
  category?: string;
};

type MetricsNavigatorProps = {
  metrics: MetricItem[];
  className?: string;
  stickyOffset?: number;
};

export function MetricsNavigator({ metrics, className, stickyOffset = 0 }: MetricsNavigatorProps) {
  const [activeMetric, setActiveMetric] = useState<string>("");
  const [allExpanded, setAllExpanded] = useState<boolean>(false);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const scrollToMetric = (metricId: string) => {
    const element = document.getElementById(`metric-${metricId}`);
    if (element) {
      const yOffset = -(stickyOffset || 0) - 16; // 16px extra for spacing
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveMetric(metricId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      // Find which metric is currently in view
      for (const metric of metrics) {
        const element = document.getElementById(`metric-${metric.id}`);
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;

          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            setActiveMetric(metric.id);
            break;
          }
        }
      }
    };

    // Throttle scroll events
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledHandleScroll);
    return () => window.removeEventListener("scroll", throttledHandleScroll);
  }, [metrics]);

  // Group metrics by category if available
  const groupedMetrics = metrics.reduce(
    (acc, metric) => {
      const category = metric.category || "Metrics";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(metric);
      return acc;
    },
    {} as Record<string, MetricItem[]>,
  );

  // Sort categories in a meaningful order
  const categoryOrder = ["Volume", "Conversions", "Revenue", "Cost", "Performance"];
  const sortedCategories = Object.keys(groupedMetrics).sort((a, b) => {
    const aIndex = categoryOrder.indexOf(a);
    const bIndex = categoryOrder.indexOf(b);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  // Initialize all categories as collapsed on first render
  const toggleAllCategories = () => {
    const newExpanded = !allExpanded;
    setAllExpanded(newExpanded);

    // Update individual category states to match the "expand all" state
    const newExpandedCategories: Record<string, boolean> = {};
    sortedCategories.forEach(category => {
      newExpandedCategories[category] = newExpanded;
    });
    setExpandedCategories(newExpandedCategories);
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const isCategoryExpanded = (category: string) => {
    return expandedCategories[category] ?? false;
  };

  return (
    <Card
      className={cn(
        "w-full shadow-lg bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        className,
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">Metrics Navigation</CardTitle>
          {sortedCategories.length > 1 && (
            <button
              onClick={toggleAllCategories}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-secondary/50"
            >
              {allExpanded ? "Collapse All" : "Expand All"}
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 max-h-[calc(100vh-12rem)] overflow-y-auto">
        {sortedCategories.map(category => (
          <Collapsible key={category} open={isCategoryExpanded(category)} onOpenChange={() => toggleCategory(category)}>
            {sortedCategories.length > 1 && (
              <CollapsibleTrigger className="w-full flex items-center justify-between px-1 py-0.5 hover:bg-secondary/50 rounded transition-colors [&[data-state=open]>div>svg]:rotate-90">
                <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{category}</h4>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <span className="text-[10px]">({groupedMetrics[category].length})</span>
                  <ChevronRight className="h-2.5 w-2.5 transition-transform duration-200" />
                </div>
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="space-y-0.5 pl-1.5 border-l border-border/30">
              {groupedMetrics[category].map(metric => (
                <button
                  key={metric.id}
                  onClick={() => scrollToMetric(metric.id)}
                  className={cn(
                    "w-full text-left px-2 py-1.5 text-xs rounded transition-all duration-200 hover:bg-secondary/80 border border-transparent",
                    activeMetric === metric.id
                      ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm border-primary/20"
                      : "text-foreground hover:border-border/50",
                  )}
                >
                  <div className="flex items-center gap-1.5">
                    <div
                      className={cn(
                        "w-1.5 h-1.5 rounded-full transition-colors",
                        activeMetric === metric.id ? "bg-primary-foreground" : "bg-muted-foreground/40",
                      )}
                    />
                    <span className="font-medium text-[11px]">{metric.label}</span>
                  </div>
                </button>
              ))}
            </CollapsibleContent>
          </Collapsible>
        ))}
      </CardContent>
    </Card>
  );
}
